/**
 * Browser-compatible data loader that fetches JSON files over HTTP
 */

import {
  DataRecord,
  Rule,
  EncyclopediaEntry,
  Ritual,
  Procedure,
} from '../types/index.js';

export class BrowserDataLoader {
  private dataBaseUrl: string;

  constructor(dataBaseUrl: string = '/data') {
    this.dataBaseUrl = dataBaseUrl;
    console.log(`📂 Loading datasets from: ${this.dataBaseUrl}`);
  }

  async loadDataset(type: 'rule'): Promise<Rule[]>;
  async loadDataset(type: 'encyclopedia'): Promise<EncyclopediaEntry[]>;
  async loadDataset(type: 'ritual'): Promise<Ritual[]>;
  async loadDataset(type: 'procedure'): Promise<Procedure[]>;
  async loadDataset(type: string): Promise<DataRecord[]> {
    const fileMap: Record<string, string> = {
      rule: 'rules.json',
      encyclopedia: 'encyclopedia.json',
      ritual: 'rituals.json',
      procedure: 'procedures.json',
    };

    const fileName = fileMap[type] || `${type}s.json`;
    const url = `${this.dataBaseUrl}/${fileName}`;

    try {
      console.log(`🔄 Fetching ${fileName}...`);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch ${fileName}: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn(`⚠️ ${fileName} returned ${contentType}, expected JSON`);
      }

      const data = await response.json();
      console.log(`  ✅ ${fileName}: ${data.length} records`);
      return data;
    } catch (error) {
      console.error(`❌ Error loading ${fileName}:`, error);
      return [];
    }
  }

  async loadAllDatasets(): Promise<DataRecord[]> {
    let allRecords: DataRecord[] = [];
    const datasetTypes: (
      | 'rule'
      | 'encyclopedia'
      | 'ritual'
      | 'procedure'
    )[] = ['rule', 'encyclopedia', 'ritual', 'procedure'];
    let totalRecords = 0;

    console.log('📚 Loading all datasets...');

    // Load all datasets in parallel for better performance
    const loadPromises = datasetTypes.map(type => this.loadDataset(type as any));
    const results = await Promise.all(loadPromises);

    for (let i = 0; i < datasetTypes.length; i++) {
      const records = results[i];
      allRecords = allRecords.concat(records);
      totalRecords += records.length;
    }

    console.log(`📚 Total records loaded: ${totalRecords}`);
    return allRecords;
  }

}
